// Shared by every mounted Job button in one authenticated Chat connection.
export function createJobChatStore(send) {
    const listeners = new Map();
    const values = new Map();
    const dirty = new Set();
    const counts = new Map();
    const countListeners = new Set();
    function updateSummaries(items, notify = false) {
        const changed = items.filter(item => { const previous = counts.get(item.id); return !previous || previous.unread_root_count !== item.unread_root_count || previous.unread_thread_count !== item.unread_thread_count; });
        if (!changed.length)
            return;
        if (pending && !notify)
            changed.forEach(item => pending.summaryUpdates.set(item.id, item));
        changed.forEach(item => counts.set(item.id, { id: item.id, unread_root_count: item.unread_root_count, unread_thread_count: item.unread_thread_count }));
        values.forEach((value, id) => {
            if (!value.status)
                return;
            const patch = (destination) => {
                const summary = destination.conversation_id ? counts.get(destination.conversation_id) : undefined;
                return summary ? { ...destination, unread_root_count: summary.unread_root_count, unread_thread_count: summary.unread_thread_count, pending_count: summary.unread_root_count + summary.unread_thread_count } : destination;
            };
            publish(id, { ...value, status: { ...value.status, property: patch(value.status.property), workers: value.status.workers.map(patch) } });
        });
        if (notify)
            countListeners.forEach(listener => listener(changed));
    }
    let connected = false, revision = 0, sequence = 0;
    let lastStarted = -Infinity, retryAt = 0, failures = 0;
    let timer;
    let pending;
    const publish = (id, value) => {
        if (!listeners.has(id))
            return;
        values.set(id, value);
        listeners.get(id)?.forEach(listener => listener(value));
    };
    function schedule() {
        if (timer || pending || !connected || document.visibilityState === 'hidden' || !dirty.size)
            return;
        timer = setTimeout(flush, Math.max(250, lastStarted + 5000 - Date.now(), retryAt - Date.now()));
    }
    function retry(ids) {
        ids.forEach(id => { if (listeners.has(id)) dirty.add(id); });
        retryAt = Date.now() + Math.min(15000, 2000 * 2 ** Math.min(failures++, 3) * (0.8 + Math.random() * 0.4));
        schedule();
    }
    function refresh(id) {
        if (id) {
            if (listeners.has(id))
                dirty.add(id);
        }
        else
            listeners.forEach((_, key) => dirty.add(key));
        schedule();
    }
    function flush() {
        timer = undefined;
        if (pending || !connected || document.visibilityState === 'hidden')
            return;
        const ids = [...dirty].filter(id => listeners.has(id)).slice(0, 100);
        if (!ids.length)
            return;
        ids.forEach(id => dirty.delete(id));
        lastStarted = Date.now();
        const id = `${revision}:${++sequence}`;
        const timeout = setTimeout(() => {
            if (pending?.id !== id)
                return;
            pending = undefined;
            ids.forEach(key => publish(key, { ...values.get(key), error: 'Chat status synchronization delayed. Retrying…' }));
            retry(ids);
        }, 10000);
        pending = { id, ids, revision, timeout, summaryUpdates: new Map() };
        if (!send({ type: 'job_channels.query', request_id: id, cos_job_ids: ids }))
            setConnected(false);
    }
    function setConnected(next) {
        connected = next;
        retryAt = 0; failures = 0;
        revision++;
        clearTimeout(timer);
        timer = undefined;
        if (pending) {
            clearTimeout(pending.timeout);
            pending = undefined;
        }
        if (next)
            refresh();
        else
            listeners.forEach((_, id) => publish(id, { ...values.get(id), error: 'Chat status not synchronized. Reconnecting…' }));
    }
    function receive(frame) {
        if (!frame.type.startsWith('job_channels.'))
            return false;
        if (!pending || frame.request_id !== pending.id)
            return true;
        const request = pending;
        clearTimeout(request.timeout);
        pending = undefined;
        if (request.revision !== revision)
            request.ids.forEach(id => { if (listeners.has(id))
                dirty.add(id); });
        else if (frame.type === 'job_channels.error') {
            request.ids.forEach(id => publish(id, { ...values.get(id), error: frame.data?.message || 'Chat status unavailable' }));
            if (!['INVALID_QUERY', 'INVALID_JOBS', 'FORBIDDEN', 'UNAUTHORIZED'].includes(frame.data?.code)) retry(request.ids);
        }
        else {
            retryAt = 0; failures = 0;
            // Keep useful mappings returned during ordinary count updates.
            // Snapshots applied since this request started own their counts;
            // explicit read/permission events still invalidate via revision.
            const patch = destination => {
                const summary = request.summaryUpdates.get(destination.conversation_id);
                return summary ? {...destination, unread_root_count: summary.unread_root_count, unread_thread_count: summary.unread_thread_count,
                    pending_count: summary.unread_root_count + summary.unread_thread_count} : destination;
            };
            const jobs = (frame.data?.jobs || []).map(job => ({...job, property: patch(job.property), workers: job.workers.map(patch)}));
            const found = new Map(jobs.map(job => [job.cos_job_id, job]));
            request.ids.forEach(id => publish(id, { status: found.get(id), error: found.has(id) ? undefined : 'Job is no longer available' }));
            updateSummaries(jobs.flatMap(job => [job.property, ...job.workers]).filter(item => item.conversation_id && item.unread_root_count !== undefined && item.unread_thread_count !== undefined).map(item => ({ id: item.conversation_id, unread_root_count: item.unread_root_count, unread_thread_count: item.unread_thread_count })), true);
        }
        schedule();
        return true;
    }
    function event(event) {
        if (!/^(message\.(created|deleted)|conversation\.|membership\.|thread\.|read\.)/.test(event.type))
            return;
        const affected = [...listeners.keys()].filter(id => {
            const status = values.get(id)?.status;
            return !status || !event.conversation_id || [status.property, ...status.workers].some(destination => destination.conversation_id === event.conversation_id);
        });
        // New/mutated channels can establish or replace a previously absent mapping.
        if (['conversation.created', 'conversation.updated', 'conversation.assignment_changed', 'conversation.access_revoked'].includes(event.type))
            listeners.forEach((_, id) => { if (!affected.includes(id))
                affected.push(id); });
        if (!affected.length)
            return;
        if (event.type !== 'message.created') revision++;
        if (event.type === 'conversation.access_revoked' && event.conversation_id)
            counts.delete(event.conversation_id);
        affected.forEach(id => {
            if (event.type === 'conversation.access_revoked')
                publish(id, { error: 'Chat access changed. Synchronizing…' });
            dirty.add(id);
        });
        schedule();
    }
    function visibility() { if (document.visibilityState === 'visible')
        refresh(); }
    return {
        receive, event, setConnected, refresh, updateSummaries,
        setTransport(next) { send = next; },
        subscribeSummaries(listener) {
            countListeners.add(listener);
            listener([...counts.values()]);
            return () => { countListeners.delete(listener); };
        },
        subscribe(id, listener) {
            if (!listeners.size)
                document.addEventListener('visibilitychange', visibility);
            if (!listeners.has(id))
                listeners.set(id, new Set());
            listeners.get(id).add(listener);
            listener(values.get(id));
            refresh(id);
            return () => {
                listeners.get(id)?.delete(listener);
                if (!listeners.get(id)?.size) {
                    listeners.delete(id);
                    values.delete(id);
                    dirty.delete(id);
                }
                if (!listeners.size) {
                    document.removeEventListener('visibilitychange', visibility);
                    clearTimeout(timer);
                    timer = undefined;
                    if (pending) {
                        clearTimeout(pending.timeout);
                        pending = undefined;
                    }
                    revision++;
                }
            };
        },
        dispose() {
            setConnected(false);
            values.clear();
            counts.clear();
            dirty.clear();
            document.removeEventListener('visibilitychange', visibility);
        },
    };
}
